import React from 'react';
import { useTranslation } from 'react-i18next';
import { Edit2 } from 'react-feather';

/**
 * Compact horizontal read-only summary of a tenant — rendered as a
 * subtitle bar above the Edit form and inside the Detail tabs header.
 * Replaces the V2 left-sidebar profile card (4 cols of empty space).
 *
 * Props:
 *   tenant — full tenant record (populated)
 *   onChangeStatus — optional click handler for the status pill
 */
const TenantProfileStrip = ({ tenant, onChangeStatus }) => {
    const { t } = useTranslation();
    if (!tenant) return null;
    const status = tenant.tenant_disable
        ? { label: 'Disabled', cls: 'off' }
        : tenant.tenant_status === 'Live'
            ? { label: 'Live', cls: 'live' }
            : tenant.tenant_status === 'UAT'
                ? { label: 'UAT', cls: 'warn' }
                : tenant.tenant_status === 'Closed' || tenant.tenant_status === 'Closed Permanently'
                    ? { label: 'Closed', cls: 'danger' }
                    : { label: tenant.tenant_status || 'Pending', cls: 'warn' };

    const Kv = ({ label, value }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span className="v3-label" style={{ margin: 0 }}>{label}</span>
            <span
                style={{
                    fontSize: 13,
                    color: 'var(--v3-text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {value || '—'}
            </span>
        </div>
    );

    return (
        <div
            className="v3-card"
            style={{
                padding: '14px 18px',
                display: 'grid',
                gridTemplateColumns: 'minmax(200px, 1fr) repeat(5, minmax(0, 1fr)) auto',
                alignItems: 'center',
                gap: 18,
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span className="v3-label" style={{ margin: 0 }}>{t('Brand')}</span>
                <h2 className="v3-title" style={{ margin: 0, fontSize: 16 }}>
                    {tenant.tenant_brand_name || tenant.tenant_store_name || '—'}
                </h2>
            </div>
            <Kv label={t('Store')} value={tenant.tenant_store_name} />
            <Kv label={t('Tenant code')} value={tenant.tenant_code} />
            <Kv label={t('Property')} value={tenant.tenant_property_id?.property_name} />
            <Kv label={t('Category')} value={tenant.tenant_category_id?.category_name} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span className="v3-label" style={{ margin: 0 }}>{t('Status')}</span>
                <button
                    type="button"
                    className={`v3-sadmin-pill ${status.cls}`}
                    onClick={onChangeStatus}
                    disabled={!onChangeStatus}
                    style={{
                        cursor: onChangeStatus ? 'pointer' : 'default',
                        border: onChangeStatus ? '1px solid transparent' : undefined,
                        gap: 6,
                    }}
                    title={onChangeStatus ? t('Change status') : ''}
                >
                    {t(status.label)}
                    {onChangeStatus && <Edit2 size={10} />}
                </button>
            </div>
        </div>
    );
};

export default TenantProfileStrip;
